/**
 * utils.js
 *
 * Utility functions useful in more than one spot. Should be accessible anywhere.
 */

import base64 from 'base-64'
import QueryString from 'qs'

/**
 * Provide a BDC Image Resizer friendly, HTTPS path for an image. This function will
 * base-64 encode the passed URL and assemble the new one. If the original image is an SVG
 * this will return the URL unchanged. Typical uses include:
 * - in an ImageResizer component
 * - as the src attribute for a traditional <img /> element
 * - as the value of an inline style declaration, e.g. backroundImage
 *
 * @param  {String} options.src - URL to original image
 * @param  {String} options.method Image resizer method. Possible values are "w", "r", and "rc"
 * @param  {Number} options.w Width in pixels to resize to
 * @param  {Number} options.h Height in pixels to resize to (optional)
 *
 * @return {String} Protocol-agnostic URL to BDC image resizer asset or default placeholder
 */
export function makeImgResizerUrl({ src, method = null, w = null, h }) {
	// return original if an SVG
	if (/.svg$/.test(src)) {
		return src
	}

	if (src === undefined || src === null || src === '' || /images\/$/.test(src)) {
		src = 'https://www.business.com/images/bdc-placeholder.jpg'
	}
	if (/^\/images\/*/.test(src)) {
		src = `${
			process.env.NODE_ENV === 'production' ? `https://www.business.com` : `https://staging.business.com`
		}${src}`
	}
	// Error if there is a bad method passed
	if (!/w|h|r|rc/.test(method)) {
		throw new Error('Bad method passed to image resizer')
	}

	// Error if height is missing when needed
	if (/rc?/.test(method) && !h) {
		throw new Error('Cannot resize image without height')
	}

	// Error if non-numeric dimensions passed
	if (!/\d/.test(w | h) || (/rc?/.test(method) && !/\d/.test(h))) {
		throw new Error('Non-numeric dimension(s) passed to image resizer')
	}

	const dims = w ? `${w}${h ? `x${h}` : ''}` : h
	return `https://img.business.com/${method}/${dims}/${base64.encode(src)}`
}

/**
 * Provide a BDC Image Resizer friendly, protocol-agnostic path for an image. This function will
 * base-64 encode the passed URL and assemble a BLURRED VERSION. Typical uses include:
 * - in an ImageResizer component
 * - as the src attribute for a traditional <img /> element
 * - as the value of an inline style declaration, e.g. backroundImage
 *
 * @param  {String} options.src - URL to original image
 * @param  {Number} options.radius - Number between 0 and 10 (the smaller the better) (https://en.wikipedia.org/wiki/Gaussian_blur)
 * @param  {Number} options.signam - How large of a radius to add blur (https://en.wikipedia.org/wiki/Gaussian_blur)
 * @param  {Number} options.w - Width in pixels to resize to (optional)
 * @param  {Number} options.h - Height in pixels to resize to (optional)
 *
 * @return {String} Protocol-agnostic URL to BDC image resizer asset or default placeholder
 */
export function makeImgResizeBlurUrl({ src, radius = 1, sigma = 1, w, h }) {
	// Error if non-numeric dimensions passed
	if (src === null || src === '' || src === '/images/' || src === 'https://www.business.com/images/') {
		src = 'https://www.business.com/images/bdc-placeholder.jpg'
	}

	if (!/\d/.test(radius) && !/\d/.test(sigma)) {
		throw new Error('Non-numeric dimension(s) passed to image blurrer')
	}

	const dims = `${radius}x${sigma}`
	return `https://img.business.com/custom/blur=${dims}${w ? `,dw=${w}` : ''}${h ? `,dh=${h}` : ''}/${base64.encode(
		src
	)}`
}

/**
 * Takes a url path and tests against 2 regexes to determine if it is an
 * external or internal link
 *
 * @param  {String} url :: The url to be tested against regexes
 *
 * @return {Boolean}
 */
export const isExternalUrl = (url) => {
	// @todo need to add a check against domain instead of regex
	const commonProtocols = new RegExp(/^https?:\/\//)
	const localCanonicalUrls = new RegExp(
		/^https?:\/\/((dev|qa|bcs|staging|www)*\.)?(business|businessnewsdaily)\.com\/?.*|^http:\/\/us-east1-dev-bcs-web1*|^http:\/\/127.0.0.1/
	)

	// a link is external if it has protocols and DOES NOT match one of the bcs internal canonical urls
	return commonProtocols.test(url) && !localCanonicalUrls.test(url)
}

/**
 * Takes a url path and returns an object defining what the the rel and target
 * values should be based on whether a url is external/internal
 *
 * @param  {String} url :: The url to be tested against regexes
 *
 * @return {Object} :: The value of link attributes
 */
export const getAdditionalLinkAttributes = (url) => {
	return !isExternalUrl(url) ? {} : { rel: 'nofollow noopener noreferrer', target: '_blank' }
}

/**
 * Promise chain helper function to check status
 * Assumes interacting with JSON response
 */
export const handleResponse = (response) =>
	response.json().then((json) => {
		if (response.ok && response.status >= 200 && response.status < 300) {
			return json
		} else {
			return Promise.reject({
				...json,
				status: response.status,
				statusText: response.statusText,
			})
		}
	})

// This is an agnostic function that's used to send AJAX requests to either
// external APIs or the Symfony tier that runs BCS
export const loadData = (endpoint) => {
	const init = {
		method: 'GET',
		headers: {
			'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=-1',
			'X-Requested-With': 'XMLHttpRequest',
		},
	}

	return fetch(endpoint, init)
		.then(handleResponse)
		.catch((ex) => {
			console.error(`Portals loadData module -- ${ex.status} error: ${ex.statusText}`)
		})
}

/**
 * Try to smoothly scroll to an element on DOM. On failure just scrollTo without any behavior type
 * Some mobile browsers don't support object signature.
 *
 * @param {object} element - the DOM node to scroll to
 */
export function smoothScrollIntoView(element) {
	try {
		element.scrollIntoView({
			behavior: 'smooth',
		})
	} catch (ex) {
		if (typeof element.scrollIntoView === 'function') {
			element.scrollIntoView()
		}
	}
}

/**
 * splits an incoming HTML string into an array of paragraphs, split on <p> tags. Also scrapes the string
 * for any double linebreak tags to replace those with paragraphs first.
 *
 * @param  {String} paragraphString :: Raw HTML string to be split
 *
 * @return {Array} of parahraph strings. Each string *assumed* to start with a "<p>" and have no trailing "</p>"
 */
export function splitParagraphString(paragraphString) {
	return paragraphString
		.trim()
		.replace(/\n(\s*\n)+/g, '</p><p>')
		.split('</p>')
		.map((e) => `${e}</p>`)
}

/**
 * Takes a passed array of strings that have been split on "</p>" and returns all paragraphs between "start" and
 * "end", properly formatted with wrapping "p" tags
 *
 * @param  {Array} paragraphs :: Each string *assumed* to start with a "<p>" and have no trailing "</p>"
 * @param  {Number} start :: 0-based index of where to start slicing "paragrahs" array
 * @param  {Number} end :: 0-based index of where to end slicing of "paragraphs" array
 *
 * @return {String} New HTML string with desired paragraphs, each wrapped in a "<p>""
 */
export const getParagraphSections = (paragraphs, start, end) => {
	const copy = paragraphs.slice(start, end)
	copy.push('')
	return copy.join('</p>').replace(/<\/p><\/p>/g, '</p>')
}

/**
 * function that returns the date and time in the required format for BI(2018-05-24 12:30:00).
 *
 * @param {Object} :: It contains all the required format values for the date to be returned.
 * @return {object} :: date returned which is built on values which are sent.
 *
 */
export function formatDate() {
	let d = new Date()
	let month = d.getMonth() + 1 > 9 ? d.getMonth() + 1 : `0${d.getMonth() + 1}`
	let date = d.getDate() > 9 ? d.getDate() : `0${d.getDate()}`
	let hours = d.getHours() > 9 ? d.getHours() : `0${d.getHours()}`
	let minutes = d.getMinutes() > 9 ? d.getMinutes() : `0${d.getMinutes()}`
	let seconds = d.getSeconds() > 9 ? d.getSeconds() : `0${d.getSeconds()}`

	return `${d.getFullYear()}-${month}-${date} ${hours}:${minutes}:${seconds}`
}

/** Converts an array of object (with multiple child objects) to array of objects
 * For example convertArrOfObjToArrOfObjs([{1: {val: 1}, 2: {val:2}}], 'id') --> [{id: 1, val: 1}, {id:2, val:2}]
 *
 * @param  {Array} dataArr :: An array containing a single object
 * @param  {string} [strippedKeyName = 'id'] :: The name of the stripped key (default value is 'id')
 *
 * @return {Array.<Object>} Returns an array of objects with the stripped key inside the child object
 */
export const convertArrOfObjToArrOfObjs = (dataArr, strippedKeyName = 'id') => {
	if (!dataArr || dataArr.length === 0) {
		return [] // return empty array
	}

	const arrayOfObjects = dataArr.reduce((accumulator, dataObj) => {
		const reducedObjs = Object.keys(dataObj).reduce(function (acc, key) {
			const currentObject = dataObj[key]

			return [
				...acc,
				{
					...currentObject,
					[strippedKeyName]: key,
				},
			]
		}, [])

		return reducedObjs
	}, [])

	return arrayOfObjects
}

/**
 * Fetch user latitude and longitude using Geolocation.
 * @returns {object} Returns an object with the keys "lat" and "lng" (default value)
 */
export function getLatAndLongByGeolocation() {
	// default object is center of the country to ensure that values are always defined
	const DEFAULT_LAT_LONG_OBJ = {
		lat: 37.751,
		lng: -97.822,
	}

	const apiKey = 'AIzaSyA5rEknjX0KqxBmVafC10L4ctWbBoSEIyE' // Geolocation Key used with call connect

	return fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`, {
		method: 'POST',
	})
		.then(handleResponse)
		.then((coordinates) => {
			const { lat, lng } = coordinates.location

			if (lat && lng) {
				return {
					lat,
					lng,
				}
			} else {
				return DEFAULT_LAT_LONG_OBJ
			}
		})
		.catch((ex) => {
			console.error(ex)
			return DEFAULT_LAT_LONG_OBJ
		})
}

/**
 * Truncate function that takes a string and checks the length
 * @param {string} text the text you want to check
 * @param {number} charLimit the character limit you want to check against
 *
 * @return {string} that appends ... if the length is greater than character limit
 */
export function truncateString(text, charLimit) {
	if (typeof text === 'undefined') {
		return ''
	}
	if (text.length <= charLimit) {
		return text
	} else {
		const truncatedText = text.substring(0, charLimit)
		return `${truncatedText.substr(0, Math.min(truncatedText.length, truncatedText.lastIndexOf(' ')))}...`
	}
}

export function isBrowser() {
	return typeof window !== `undefined`
}

export function handleModalCloseButton(modalVisible, setModalVisible) {
	if (modalVisible) {
		setModalVisible(false)
	} else {
		window.location = '/'
	}
}

export const isPastScrollYMark = (percentage, setScroll) => {
	if (!isBrowser()) {
		return
	}
	// handle various cases in different browsers, devices, and so on.
	const yScrollMark =
		Math.max(
			document.body.scrollHeight,
			document.documentElement.scrollHeight,
			document.body.offsetHeight,
			document.documentElement.offsetHeight,
			document.body.clientHeight,
			document.documentElement.clientHeight
		) * percentage
	const handleScroll = () => setScroll(window.pageYOffset >= yScrollMark)
	window.addEventListener('scroll', handleScroll)

	return () => {
		window.removeEventListener('scroll', handleScroll)
	}
}

export function replaceSpacesWithDashes(text) {
	return text.replace(/\s/g, '-')
}

export function flattenArray(array) {
	return array.reduce((accumulator, currentValue) => accumulator.concat(currentValue), [])
}

export function generateLinkWithAddedParams({ clientLocation, link, rank, type, tabTitle }) {
	// check if rank is number -- add 1 to make rank non zero based
	if (typeof rank === 'number') {
		rank++
	}
	if (link !== null && link !== undefined && link.includes('businesscom.go2cloud.org')) {
		let aff_sub1 = 'bestpick|no'
		if (tabTitle !== undefined && tabTitle !== 'undefined') {
			aff_sub1 = `bestpick|${tabTitle.replace(/\s+/g, '-').toLowerCase()}`
		}

		const encodedCurrentUrl = encodeURIComponent(clientLocation || (isBrowser() ? window.location.href : ''))
		let additionalQueryStringParams = QueryString.stringify(
			{
				aff_sub: aff_sub1,
				aff_sub2: encodedCurrentUrl, // falling back to document.referrer (which should always be the page serving the iframe widget)
				aff_sub5: `pos|bbc${rank}|${type}`,
			},
			{ encode: false, indices: false }
		)
		return `${link}&${additionalQueryStringParams}`
	} else {
		return link
	}
}

/**
 * Function that checks monetization data based on the pricing.selected type
 * returns affiliate link if pricing.selected type is valid
 * returns null if pricing.selected type is invalid
 *
 * @param {Object} pricing :: object containing data that sets monetization buttons
 */
export function generateAffiliateLink(pricing) {
	let affiliateLink = null
	const affiliateTypes = ['affiliate', 'affiliate-widget', 'widget-affiliate']

	if (pricing && affiliateTypes.includes(pricing.selected)) {
		affiliateLink = pricing?.affiliate?.link || null
	}

	return affiliateLink
}

/**
 * Strip out html code from string
 */

export function stripOutHtml(string) {
	if (string === null || string === '') {
		return string
	} else {
		return string.replace(/<[^>]*>/g, '')
	}
}

/**
 * Function that groups content by their displayModified field
 * returns an object with `YYYY-MM-DD` as keys and arrays of content as values
 *
 * @param {Array} data :: array containing data that contains displayModified field we use to group content together by
 */
export function groupContentByDate(data) {
	return data.reduce((acc, day) => {
		// Get the year-month-day
		const date = day.displayModified.split('T')[0]
		if (!acc[date]) {
			acc[date] = []
		}
		acc[date].push(day)
		return acc
	}, {})
}
